import { Ref, useCallback, useImperativeHandle, useMemo } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { validateArrayType } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import SelectMultipleListItem, {
  MultipleListItem,
} from "@coworker/apprestructured/src/task-management/components/SelectMultipleListItem/SelectMultipleListItem";
import { shouldStoreValueOnly } from "@coworker/apprestructured/src/shared/helpers/task.helper";
import { useTranslation } from "@coworker/locales";
import { shouldShowField } from "@coworker/apprestructured/src/task-management/helpers/visibilityCondition.helper";

export type SelectMultipleListItemCreatorFragmentConfigProps = {
  title: string;
  field: string;
  items: MultipleListItem[];
  minItems?: number;
  max?: number;
  maxItemsWarningKey?: string;
  search?: boolean;
  label?: string;
};

type SelectMultipleListItemCreatorFragmentProps = {
  configProps: SelectMultipleListItemCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function SelectMultipleListItemCreatorFragment({
  configProps,
  commonProps,
  ref,
}: SelectMultipleListItemCreatorFragmentProps) {
  const {
    field,
    title,
    items,
    minItems = 0,
    max,
    maxItemsWarningKey,
    search,
    label,
  } = configProps;
  const { task, onTaskChange, taskType, showValidation } = commonProps;
  const { t } = useTranslation();

  const visibleItems = useMemo(
    () =>
      isLoading(task)
        ? items
        : items.filter(
            (item) => !item.visibility || shouldShowField(task, item.visibility)
          ),
    [items, task]
  );

  const rawSelectedItems = getByPath(task, field);
  const storeValueOnly = shouldStoreValueOnly(taskType, field);

  // Handle both object array and string array formats
  const selectedItems = (() => {
    if (isLoading(rawSelectedItems)) return rawSelectedItems;
    if (!Array.isArray(rawSelectedItems)) return [];

    // If storeValueOnly and we have strings, convert to objects for display
    if (
      storeValueOnly &&
      rawSelectedItems.every((item) => typeof item === "string")
    ) {
      return rawSelectedItems
        .map((value) => visibleItems.find((item) => item.value === value))
        .filter((item): item is MultipleListItem => item !== undefined);
    }

    return validateArrayType<MultipleListItem>(
      rawSelectedItems,
      field,
      taskType
    );
  })();

  const resolveSelectedItems = useCallback((): MultipleListItem[] => {
    if (isLoading(selectedItems) || !Array.isArray(selectedItems)) {
      return [];
    }

    return selectedItems.filter((item) =>
      visibleItems.some((availableItem) => availableItem.value === item.value)
    );
  }, [visibleItems, selectedItems]);

  const currentSelectedItems = resolveSelectedItems();

  const handleConfirmClick = useCallback(
    (newItems: MultipleListItem[]) => {
      const valueToStore = storeValueOnly
        ? newItems.map((item) => item.value)
        : newItems.map((item) => ({ value: item.value, label: item.label }));
      onTaskChange(setByPath(field, valueToStore));
    },
    [field, onTaskChange, storeValueOnly]
  );

  const valid =
    !isLoading(selectedItems) &&
    currentSelectedItems.length >= minItems &&
    (max === undefined || currentSelectedItems.length <= max);

  useImperativeHandle(ref, () => {
    return { valid };
  });

  if (isLoading(selectedItems)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <SelectMultipleListItem
      title={title}
      selectedItems={currentSelectedItems}
      items={visibleItems}
      onConfirmClick={handleConfirmClick}
      maxItems={max}
      maxItemsWarningKey={maxItemsWarningKey}
      search={search}
      label={label}
      showError={showValidation && !valid}
      errorMessage={t("pleaseSelectString")}
    />
  );
}

export default SelectMultipleListItemCreatorFragment;
